import { useState, useEffect } from "react";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import PropertySection from "../PropertySection";
import type { VideoSettings } from "../hooks/usePropertyPanelState";

interface AudioSettingsSectionProps {
  audioSettings: VideoSettings;
  onSettingChange: (key: keyof VideoSettings, value: boolean | string) => void;
  elementAttributes?: Record<string, any>;
  onAttributeChange?: (key: string, value: boolean | string) => void;
  disabled?: boolean;
}

const BOOLEAN_TOGGLES: { key: keyof VideoSettings; label: string }[] = [
  { key: "autoplay", label: "Autoplay" },
  { key: "muted", label: "Muted" },
  { key: "loop", label: "Loop" },
  { key: "controls", label: "Controls" },
];

const CONTROL_PARTS: { attr: string; label: string }[] = [
  { attr: "data-hide-playpause", label: "Play/Pause" },
  { attr: "data-hide-track", label: "Track" },
  { attr: "data-hide-time", label: "Time" },
  { attr: "data-hide-volume", label: "Volume" },
  { attr: "data-hide-overflow", label: "More Menu" },
];

export const AudioSettingsSection = ({
  audioSettings,
  onSettingChange,
  elementAttributes,
  onAttributeChange,
  disabled = false,
}: AudioSettingsSectionProps): React.ReactElement => {
  // Track hidden state locally so UI updates immediately on click
  const [hiddenParts, setHiddenParts] = useState<Record<string, boolean>>({});

  // Sync from element attributes when element changes
  useEffect(() => {
    const state: Record<string, boolean> = {};
    for (const { attr } of CONTROL_PARTS) {
      state[attr] =
        elementAttributes?.[attr] !== undefined &&
        elementAttributes?.[attr] !== null;
    }
    setHiddenParts(state);
  }, [elementAttributes]);

  const handleToggle = (attr: string, hide: boolean) => {
    setHiddenParts((prev) => ({ ...prev, [attr]: hide }));
    // false removes the attribute (show), "" sets it (hide)
    onAttributeChange?.(attr, hide ? "" : false);
  };

  return (
    <PropertySection title="Audio Settings">
      <div className="flex flex-col gap-3">
        {/* Playback toggles */}
        <div className="grid grid-cols-2 gap-3">
          {BOOLEAN_TOGGLES.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between gap-2">
              <Label className="text-xs text-muted-foreground">{label}</Label>
              <Switch
                checked={audioSettings[key] as boolean}
                onCheckedChange={(checked) => onSettingChange(key, checked)}
                disabled={disabled}
              />
            </div>
          ))}
        </div>

        {/* Controls Visibility — same grid layout as toggles above */}
        {onAttributeChange && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {CONTROL_PARTS.map(({ attr, label }) => {
                const isHidden = hiddenParts[attr] ?? false;
                return (
                  <div
                    key={attr}
                    className="flex items-center justify-between gap-2"
                  >
                    <Label className="text-xs text-muted-foreground">
                      {label}
                    </Label>
                    <Switch
                      checked={!isHidden}
                      onCheckedChange={(checked) =>
                        handleToggle(attr, !checked)
                      }
                      disabled={disabled}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </PropertySection>
  );
};
